import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { widgetsAPI, locationsAPI, reviewsAPI } from '../services/api';
import { getPlatformsWithIcons } from '../utils/platformIcons';
import usePageTitle from '../hooks/usePageTitle';

// Import new components
import CreateLocationModal from '../components/CreateWidget/CreateLocationModal';
import Step1Layout from '../components/CreateWidget/Step1Layout';
import Step2Configure from '../components/CreateWidget/Step2Configure';
import Step3Deploy from '../components/CreateWidget/Step3Deploy';
import WhatsAppWidgetConfig from '../components/CreateWidget/WhatsAppWidgetConfig';
import AppointmentWidgetConfig from '../components/CreateWidget/AppointmentWidgetConfig';
import DashboardLayout from '../components/DashboardLayout';

// Validation helper for appointment widgets
const validateAppointmentWidget = (widgetConfig) => {
    const config = widgetConfig.appointmentConfig || {};

    // Check widget title (Design tab)
    if (!config.widgetTitle?.trim()) {
        return {
            isValid: false,
            targetTab: 'design',
            message: 'Please enter a widget title to identify your booking form.',
            missingFields: ['widgetTitle']
        };
    }

    // Validate service details only when services are configured
    const services = config.services || [];
    if (services.length > 0) {
        const invalidServices = services.filter(
            service => !service.name?.trim() || !service.duration
        );
        if (invalidServices.length > 0) {
            return {
                isValid: false,
                targetTab: 'services',
                message: 'Please ensure all services have a name and duration.',
                missingFields: ['serviceDetails']
            };
        }
    }

    // Check available hours (Hours tab)
    const availableHours = config.availableHours || {};
    const hasAvailableDay = Object.values(availableHours).some(day => day.available);
    if (!hasAvailableDay) {
        return {
            isValid: false,
            targetTab: 'hours',
            message: 'Please set at least one day as available for appointments.',
            missingFields: ['availableHours']
        };
    }

    // Check notifications configuration (Notifications tab)
    if (config.notifications?.ownerNotification && !config.notifications?.ownerEmail?.trim()) {
        return {
            isValid: false,
            targetTab: 'notifications',
            message: 'Please enter an owner email address to receive booking notifications.',
            missingFields: ['ownerEmail']
        };
    }

    return { isValid: true };
};

// Success Modal Component
const UpdateSuccessModal = ({ isOpen, onClose, onRedirect }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl border border-green-200">
                <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Widget Updated Successfully!</h3>
                    <p className="text-gray-600 mb-6">Your widget has been updated and is ready to use.</p>
                    <div className="space-y-3">
                        <button
                            onClick={onRedirect}
                            className="w-full bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors font-medium"
                        >
                            Go to Widgets List
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                        >
                            Stay Here
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Error Modal Component
const ErrorModal = ({ isOpen, onClose, errorMessage }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl border border-red-200">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
                    <p className="text-red-600 mb-6">{errorMessage}</p>
                    <button
                        onClick={onClose}
                        className="w-full bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-colors font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const CreateWidget = () => {
    usePageTitle('Create Widget');
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [previewMode, setPreviewMode] = useState('desktop');

    // Add state for editing mode
    const [isEditingMode, setIsEditingMode] = useState(false);
    const [editingWidgetId, setEditingWidgetId] = useState(null);
    const [loadingExistingWidget, setLoadingExistingWidget] = useState(false);

    // Ref for AppointmentWidgetConfig component
    const appointmentWidgetConfigRef = useRef();

    // Widget configuration state
    const [widgetConfig, setWidgetConfig] = useState({
        name: '',
        type: '',
        selectedTemplate: null,
        selectedLayout: 'carousel',
        platform: 'all',
        selectedPlatforms: [],
        locations: [],
        selectedLocations: [],
        minRating: 1,
        hideReviewsWithoutComments: false,

        settings: {
            maxReviews: 5,
            showRating: true,
            showAvatar: true,
            showDate: true,
            showPlatformLogo: true,
            autoSlide: false,
            slideInterval: 5000,
            theme: 'light',
            primaryColor: '#3b82f6',
            borderRadius: 8
        },

        // Header configuration with defaults
        headerConfig: {
            showHeader: true,
            elements: {
                heading: true,
                rating: true,
                numberOfReviews: true,
                writeReviewButton: true
            }
        },
        tabsConfig: {
            showTabs: true,
            elements: {
                icon: true,
                name: true,
                rating: true
            }
        },
        widgetTitleConfig: {
            showTitle: true,
            title: "What our customers say",
            caption: "Hello this is just a demo"
        },

        // Default style configuration for new widgets
        style: {
            colorScheme: 'light',
            accentColor: '#4A90E2',
            fontFamily: 'default',
            background: {
                enabled: false,
                type: 'color',
                color: '#FFFFFF',
                gradient: 'linear-gradient(45deg, #4A90E2, #9B59B6)'
            },
            widgetTitle: {
                alignment: 'center',
                titleColor: '#000000',
                captionColor: '#666666',
                titleSize: 24,
                titleBold: false,
                titleItalic: false,
                captionSize: 15,
                captionBold: false,
                captionItalic: false
            },
            header: {
                backgroundColor: '#FFFFFF',
                textColor: '#000000',
                sourceLogoColor: '#FF6B6B',
                tabSourceIconColor: '#FF6B6B',
                tabTextSize: 14,
                cornerRadius: 8
            },
            rating: {
                starsColor: '#F1C40F'
            },
            review: {
                backgroundColor: '#FFFFFF',
                outlineColor: '#E5E5E5',
                textColor: '#000000',
                reviewerNameColor: '#000000',
                dateColor: '#666666',
                textSize: 16,
                cornerRadius: 8
            },
            pagination: {
                color: '#4A90E2'
            }
        }
    });

    const [createdWidget, setCreatedWidget] = useState(null);
    const [platformCounts, setPlatformCounts] = useState([]);
    const [loadingPlatformCounts, setLoadingPlatformCounts] = useState(false);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
    const [creatingLocation, setCreatingLocation] = useState(false);
    const [previewReviews, setPreviewReviews] = useState([]);
    const [loadingPreviewReviews, setLoadingPreviewReviews] = useState(false);
    const [locationForm, setLocationForm] = useState({
        name: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'United States'
        }
    });

    const [showUpdateSuccessModal, setShowUpdateSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Function to load existing widget data for editing
    const loadExistingWidget = async (widgetId) => {
        setLoadingExistingWidget(true);
        try {
            const response = await widgetsAPI.getWidget(widgetId);
            const widget = response.data;

            // Get complete template data
            const templates = [
                { id: 'carousel-widget', name: 'Carousel Widget', image: '/review_widgets/Carousel Widget.png', category: 'carousel' },
                { id: 'grid', name: 'Grid', image: '/review_widgets/Grid.png', category: 'grid' },
                { id: 'badge', name: 'Badge', image: '/review_widgets/Badge.png', category: 'badge' },
                { id: 'simple-carousel', name: 'Simple Carousel', image: '/review_widgets/Simple Carousel.png', category: 'carousel' },
                { id: 'floating-badge', name: 'Floating Badge', image: '/review_widgets/Floating Badge.png', category: 'badge' },
                { id: 'reviews-carousel', name: 'Reviews Carousel', image: '/review_widgets/Review Carousel.png', category: 'carousel' },
                { id: 'list', name: 'List', image: '/review_widgets/List.png', category: 'list' },
                { id: 'mansory', name: 'Masonry', image: '/review_widgets/Mansory.png', category: 'grid' },
                { id: 'sidebar-widget', name: 'Sidebar Widget', image: '/review_widgets/Sidebar Widget.png', category: 'sidebar' },
                { id: 'slider', name: 'Slider', image: '/review_widgets/Slider.png', category: 'carousel' },
                { id: 'dark-carousel', name: 'Dark Carousel', image: '/review_widgets/Dark Carousel.png', category: 'carousel' },
                { id: 'dark-grid', name: 'Dark Grid', image: '/review_widgets/Dark Grid.png', category: 'grid' },
                { id: 'dark-floating-badge', name: 'Dark Floating Badge', image: '/review_widgets/Dark Floating Badge.png', category: 'badge' }
            ];

            const template = templates.find(t => t.id === widget.type) || widget.template;

            // Populate the widget configuration with existing data
            setWidgetConfig({
                name: widget.name || '',
                type: widget.type || '',
                selectedTemplate: template,
                selectedLayout: widget.settings?.layout || 'carousel',
                platform: 'all',
                selectedPlatforms: widget.selectedPlatforms || [],
                locations: widget.locations || [],
                selectedLocations: widget.locations?.map(loc => loc.locationId || loc._id) || [],
                minRating: widget.settings?.minRating || 1,
                hideReviewsWithoutComments: widget.settings?.hideReviewsWithoutComments || false,

                settings: {
                    maxReviews: widget.settings?.maxReviews || 5,
                    showRating: widget.settings?.showRating !== undefined ? widget.settings.showRating : true,
                    showAvatar: widget.settings?.showAvatar !== undefined ? widget.settings.showAvatar : true,
                    showDate: widget.settings?.showDate !== undefined ? widget.settings.showDate : true,
                    showPlatformLogo: widget.settings?.showPlatformLogo !== undefined ? widget.settings.showPlatformLogo : true,
                    autoSlide: widget.settings?.autoSlide || false,
                    slideInterval: widget.settings?.slideInterval || 5000,
                    theme: widget.settings?.theme || 'light',
                    primaryColor: widget.settings?.primaryColor || '#3b82f6',
                    borderRadius: widget.settings?.borderRadius || 8
                },

                // Load header configuration from existing widget
                headerConfig: widget.headerConfig || {
                    showHeader: true,
                    elements: {
                        heading: true,
                        rating: true,
                        numberOfReviews: true,
                        writeReviewButton: true
                    }
                },
                tabsConfig: widget.tabsConfig || {
                    showTabs: true,
                    elements: {
                        icon: true,
                        name: true,
                        rating: true
                    }
                },
                widgetTitleConfig: widget.widgetTitleConfig || {
                    showTitle: true,
                    title: "What our customers say",
                    caption: "Hello this is just a demo"
                },

                // Load style configuration from existing widget
                style: {
                    // For appointment widgets, load from the top-level styleConfig
                    ...(widget.type === 'appointment' && widget.styleConfig ? {
                        backgroundColor: widget.styleConfig.backgroundColor || '#FFFFFF',
                        textColor: widget.styleConfig.textColor || '#000000',
                        accentColor: widget.styleConfig.accentColor || '#4A90E2',
                        fontFamily: widget.styleConfig.fontFamily || 'default'
                    } : {}),
                    // For other widgets, use the nested structure
                    ...(widget.styleConfig || {
                        colorScheme: 'light',
                        accentColor: '#4A90E2',
                        fontFamily: 'default',
                        background: {
                            enabled: false,
                            type: 'color',
                            color: '#FFFFFF',
                            gradient: 'linear-gradient(45deg, #4A90E2, #9B59B6)'
                        },
                        widgetTitle: {
                            alignment: 'center',
                            titleColor: '#000000',
                            captionColor: '#666666',
                            titleSize: 24,
                            titleBold: false,
                            titleItalic: false,
                            captionSize: 15,
                            captionBold: false,
                            captionItalic: false
                        },
                        header: {
                            backgroundColor: '#FFFFFF',
                            textColor: '#000000',
                            sourceLogoColor: '#FF6B6B',
                            tabSourceIconColor: '#FF6B6B',
                            tabTextSize: 14,
                            cornerRadius: 8
                        },
                        rating: {
                            starsColor: widget.styleConfig?.rating?.starsColor || '#F1C40F'
                        },
                        review: {
                            backgroundColor: '#FFFFFF',
                            outlineColor: '#E5E5E5',
                            textColor: '#000000',
                            reviewerNameColor: '#000000',
                            dateColor: '#666666',
                            textSize: 16,
                            cornerRadius: 8
                        },
                        pagination: {
                            color: '#4A90E2'
                        }
                    })
                },

                // Load WhatsApp configuration for WhatsApp widgets
                whatsappConfig: widget.type === 'whatsapp' ? {
                    phoneNumber: widget.whatsappConfig?.phoneNumber || '',
                    message: widget.whatsappConfig?.message || "Hi! I'd like to know more about your services.",
                    welcomeMessage: widget.whatsappConfig?.welcomeMessage || "Hi there 👋\n\nHow can I help you?",
                    showTooltip: widget.whatsappConfig?.showTooltip !== undefined ? widget.whatsappConfig.showTooltip : true,
                    tooltipText: widget.whatsappConfig?.tooltipText || 'Chat with us!',
                    size: widget.whatsappConfig?.size || 'medium',
                    position: widget.whatsappConfig?.position || 'bottom-right',
                    profilePicture: widget.whatsappConfig?.profilePicture || '',
                    name: widget.whatsappConfig?.name || '',
                    captionType: widget.whatsappConfig?.captionType || 'online',
                    captionText: widget.whatsappConfig?.captionText || 'Online',
                    startChatMethod: widget.whatsappConfig?.startChatMethod || 'button',
                    buttonText: widget.whatsappConfig?.buttonText || 'Start Chat',
                    showButtonIcon: widget.whatsappConfig?.showButtonIcon !== undefined ? widget.whatsappConfig.showButtonIcon : true,
                    messageFieldPlaceholder: widget.whatsappConfig?.messageFieldPlaceholder || 'Type your message...',
                    includePageURL: widget.whatsappConfig?.includePageURL || false
                } : {},

                // Load Appointment configuration for Appointment widgets
                appointmentConfig: widget.type === 'appointment' ? {
                    templateType: widget.appointmentConfig?.templateType || 'inline-form',
                    widgetTitle: widget.appointmentConfig?.widgetTitle || 'Book Your Appointment',
                    widgetWidth: widget.appointmentConfig?.widgetWidth || '1240',
                    dateDisplayMode: widget.appointmentConfig?.dateDisplayMode || 'horizontal',
                    tracking: widget.appointmentConfig?.tracking || {
                        googleAds: { enabled: false, conversionId: '', conversionLabel: '' },
                        customCode: ''
                    },
                    services: widget.appointmentConfig?.services || [],
                    serviceDisplayView: widget.appointmentConfig?.serviceDisplayView || 'list',
                    businessProfile: widget.appointmentConfig?.businessProfile || {
                        showProfile: true,
                        businessName: '',
                        businessDescription: '',
                        coverImage: '',
                        logo: '',
                        contactInfo: {
                            phone: '',
                            email: '',
                            website: '',
                            address: ''
                        }
                    },
                    availableHours: (() => {
                        // Handle backwards compatibility for different property structures
                        const hours = widget.appointmentConfig?.availableHours || widget.appointmentConfig?.hoursConfig;
                        if (hours) {
                            // Convert old 'enabled' property to 'available' if needed
                            const convertedHours = {};
                            Object.keys(hours).forEach(day => {
                                convertedHours[day] = {
                                    available: hours[day].available !== undefined ? hours[day].available : hours[day].enabled,
                                    start: hours[day].start || '10:00',
                                    end: hours[day].end || '19:00'
                                };
                            });
                            return convertedHours;
                        }
                        // Default configuration
                        return {
                            monday: { available: true, start: '10:00', end: '19:00' },
                            tuesday: { available: true, start: '10:00', end: '19:00' },
                            wednesday: { available: true, start: '10:00', end: '19:00' },
                            thursday: { available: true, start: '10:00', end: '19:00' },
                            friday: { available: true, start: '10:00', end: '19:00' },
                            saturday: { available: true, start: '10:00', end: '19:00' },
                            sunday: { available: true, start: '10:00', end: '19:00' }
                        };
                    })(),
                    dateSpecificHours: widget.appointmentConfig?.dateSpecificHours || {},
                    minAdvanceBooking: widget.appointmentConfig?.minAdvanceBooking || { value: 1, unit: 'hour' },
                    maxAdvanceBooking: widget.appointmentConfig?.maxAdvanceBooking || { value: 2, unit: 'months' },
                    timezone: widget.appointmentConfig?.timezone || '(GMT+05:30) Asia/Colombo',
                    integrations: widget.appointmentConfig?.integrations || {
                        googleCalendar: { enabled: false, calendars: [] },
                        googleMeet: { enabled: false },
                        zoom: { enabled: false },
                        outlook: { enabled: false }
                    },
                    notifications: widget.appointmentConfig?.notifications || {
                        customerNotifications: { enabled: true, emailTemplate: '' },
                        businessNotifications: { enabled: true, emailTemplate: '' }
                    },
                    settings: widget.appointmentConfig?.settings || {
                        confirmationRequired: true,
                        rescheduleEnabled: true,
                        cancelationEnabled: true,
                        reminderEnabled: true,
                        reminderTime: '24'
                    }
                } : {}
            });

            // Set the step to 2 (configure) for editing
            setCurrentStep(2);

        } catch (error) {
            console.error('Error loading existing widget:', error);
            alert('Failed to load widget for editing. Please try again.');
        } finally {
            setLoadingExistingWidget(false);
        }
    };

    // Initialize with URL parameters and check for edit mode
    useEffect(() => {
        const templateId = searchParams.get('template');
        const step = searchParams.get('step');
        const editWidgetId = searchParams.get('edit');
        const widgetType = searchParams.get('type');

        // Handle edit mode
        if (editWidgetId) {
            setIsEditingMode(true);
            setEditingWidgetId(editWidgetId);
            loadExistingWidget(editWidgetId);
        } else if (widgetType === 'whatsapp') {
            // Handle WhatsApp widget type - skip Step 1, go directly to configuration
            setWidgetConfig(prev => ({
                ...prev,
                type: 'whatsapp',
                name: 'WhatsApp Chat Widget',
                whatsappConfig: {
                    phoneNumber: '',
                    message: "Hi! I'd like to know more about your services.",
                    welcomeMessage: "Hi there 👋\n\nHow can I help you?",
                    showTooltip: true,
                    tooltipText: 'Chat with us!',
                    size: 'medium',
                    position: 'bottom-right'
                },
                style: {
                    ...prev.style,
                    backgroundColor: '#25D366'
                }
            }));
            // Skip Step 1 for WhatsApp widgets, go directly to configuration
            setCurrentStep(2);
        } else if (widgetType === 'appointment') {
            // Handle Appointment widget type - skip Step 1, go directly to configuration
            setWidgetConfig(prev => ({
                ...prev,
                type: 'appointment',
                name: 'Appointment Booking Widget',
                appointmentConfig: {
                    templateType: 'inline-form',
                    widgetTitle: 'Book Your Appointment',
                    dateDisplayMode: 'horizontal',
                    tracking: {
                        googleAds: { enabled: false, conversionId: '', conversionLabel: '' },
                        customCode: ''
                    },
                    services: [],
                    availableHours: {
                        monday: { available: true, start: '10:00', end: '19:00' },
                        tuesday: { available: true, start: '10:00', end: '19:00' },
                        wednesday: { available: true, start: '10:00', end: '19:00' },
                        thursday: { available: true, start: '10:00', end: '19:00' },
                        friday: { available: true, start: '10:00', end: '19:00' },
                        saturday: { available: true, start: '10:00', end: '19:00' },
                        sunday: { available: true, start: '10:00', end: '19:00' }
                    },
                    dateSpecificHours: [],
                    minAdvanceBooking: { value: 1, unit: 'hour' },
                    maxAdvanceBooking: { value: 2, unit: 'months' },
                    timezone: '(GMT+05:30) Asia/Colombo',
                    businessProfile: {
                        showProfile: true,
                        businessName: '',
                        description: '',
                        contact: {}
                    },
                    notifications: {
                        clientConfirmation: true,
                        ownerNotification: true,
                        ownerEmail: ''
                    },
                    integrations: {},
                    settings: {
                        language: 'en-US',
                        customTexts: {}
                    }
                },
                style: {
                    ...prev.style,
                    backgroundColor: '#FFFFFF',
                    textColor: '#000000',
                    accentColor: '#4A90E2'
                }
            }));
            // Skip Step 1 for Appointment widgets, go directly to configuration
            setCurrentStep(2);
        } else if (templateId) {
            // Get complete template data for URL initialization (review widgets)
            const templates = [
                { id: 'carousel-widget', name: 'Carousel Widget', image: '/review_widgets/Carousel Widget.png', category: 'carousel' },
                { id: 'grid', name: 'Grid', image: '/review_widgets/Grid.png', category: 'grid' },
                { id: 'badge', name: 'Badge', image: '/review_widgets/Badge.png', category: 'badge' },
                { id: 'simple-carousel', name: 'Simple Carousel', image: '/review_widgets/Simple Carousel.png', category: 'carousel' },
                { id: 'floating-badge', name: 'Floating Badge', image: '/review_widgets/Floating Badge.png', category: 'badge' },
                { id: 'reviews-carousel', name: 'Reviews Carousel', image: '/review_widgets/Review Carousel.png', category: 'carousel' },
                { id: 'list', name: 'List', image: '/review_widgets/List.png', category: 'list' },
                { id: 'mansory', name: 'Masonry', image: '/review_widgets/Mansory.png', category: 'grid' },
                { id: 'sidebar-widget', name: 'Sidebar Widget', image: '/review_widgets/Sidebar Widget.png', category: 'sidebar' },
                { id: 'slider', name: 'Slider', image: '/review_widgets/Slider.png', category: 'carousel' }
                // Dark theme variants removed - components handle theming internally
            ];

            const template = templates.find(t => t.id === templateId);
            if (template) {
                setWidgetConfig(prev => ({
                    ...prev,
                    type: templateId,
                    selectedTemplate: template // Set complete template object
                }));
            }
        }

        if (step && ['1', '2', '3'].includes(step)) {
            setCurrentStep(parseInt(step));
        }
    }, [searchParams]);

    // Update URL when step changes
    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('step', currentStep.toString());

        if (widgetConfig.type) {
            newSearchParams.set('template', widgetConfig.type);
        }

        setSearchParams(newSearchParams, { replace: true });
    }, [currentStep, widgetConfig.type, setSearchParams]);

    // Load platform counts and locations when component mounts
    useEffect(() => {
        const loadPlatformCounts = async (locationIds = null) => {
            setLoadingPlatformCounts(true);
            try {
                const response = await widgetsAPI.getPlatformCounts(locationIds);

                // Handle the actual response format - check if it's wrapped or direct array
                let platformCountsData;
                if (Array.isArray(response.data)) {
                    // Direct array response
                    platformCountsData = response.data;
                } else if (response.data && response.data.data) {
                    // Wrapped response format { success: true, data: [...] }
                    platformCountsData = response.data.data;
                } else {
                    // Fallback
                    platformCountsData = [];
                }
                setPlatformCounts(platformCountsData);
            } catch (error) {
                console.error('Error loading platform counts:', error);
                setPlatformCounts([]);
            } finally {
                setLoadingPlatformCounts(false);
            }
        };

        const loadLocations = async () => {
            setLoadingLocations(true);
            try {
                // Use getLocationsForWidget to auto-sync locations from shared API
                const response = await locationsAPI.getLocationsForWidget();
                setAvailableLocations(response.data.data || []);
            } catch (error) {
                console.error('Error loading locations:', error);
                setAvailableLocations([]);
            } finally {
                setLoadingLocations(false);
            }
        };

        loadPlatformCounts();
        loadLocations();
    }, []);

    // Reload platform counts when selected locations change
    useEffect(() => {
        const loadPlatformCounts = async (locationIds = null) => {
            setLoadingPlatformCounts(true);
            try {
                const response = await widgetsAPI.getPlatformCounts(locationIds);

                // Handle the actual response format - check if it's wrapped or direct array
                let platformCountsData;
                if (Array.isArray(response.data)) {
                    // Direct array response
                    platformCountsData = response.data;
                } else if (response.data && response.data.data) {
                    // Wrapped response format { success: true, data: [...] }
                    platformCountsData = response.data.data;
                } else {
                    // Fallback
                    platformCountsData = [];
                }
                setPlatformCounts(platformCountsData);
            } catch (error) {
                console.error('Error loading platform counts:', error);
                setPlatformCounts([]);
            } finally {
                setLoadingPlatformCounts(false);
            }
        };

        // Only reload platform counts if locations are selected
        if (widgetConfig.selectedLocations.length > 0) {
            loadPlatformCounts(widgetConfig.selectedLocations);
        } else {
            // If no locations selected, load all platform counts
            loadPlatformCounts();
        }
    }, [widgetConfig.selectedLocations]);

    // Load preview reviews when platforms or locations change
    useEffect(() => {
        const loadPreviewReviews = async () => {
            if (widgetConfig.selectedPlatforms.length === 0) {
                setPreviewReviews([]);
                return;
            }

            setLoadingPreviewReviews(true);
            try {
                // Build query parameters with comma-separated values
                const queryParams = {
                    limit: 100, // Increased limit to get more reviews
                    sortBy: 'reviewDate',
                    sortOrder: 'desc'
                };

                // Add comma-separated platforms
                if (widgetConfig.selectedPlatforms.length > 0) {
                    queryParams.platform = widgetConfig.selectedPlatforms.join(',');
                }

                // Add comma-separated location IDs
                if (widgetConfig.selectedLocations.length > 0) {
                    queryParams.locationId = widgetConfig.selectedLocations.join(',');
                }

                const response = await reviewsAPI.getReviews(queryParams);

                // Handle paginated response structure - the actual reviews are in docs
                let reviews = [];
                if (response.data && response.data.data) {
                    if (Array.isArray(response.data.data)) {
                        // Direct array response
                        reviews = response.data.data;
                    } else if (response.data.data.docs && Array.isArray(response.data.data.docs)) {
                        // Paginated response - actual reviews are in docs
                        reviews = response.data.data.docs;
                    }
                }

                // Apply minimum rating filter
                if (widgetConfig.minRating > 1 && Array.isArray(reviews)) {
                    reviews = reviews.filter(review => review.rating >= widgetConfig.minRating);
                }

                // Hide reviews without comments if configured
                if (widgetConfig.hideReviewsWithoutComments && Array.isArray(reviews)) {
                    reviews = reviews.filter(review => review.hasComment && review.reviewText?.trim());
                }
                setPreviewReviews(reviews);
            } catch (error) {
                console.error('Error loading preview reviews:', error);
                setPreviewReviews([]);
            } finally {
                setLoadingPreviewReviews(false);
            }
        };

        // Only load preview reviews if platforms are selected
        if (widgetConfig.selectedPlatforms.length > 0) {
            loadPreviewReviews();
        } else {
            setPreviewReviews([]);
        }
    }, [widgetConfig.selectedPlatforms, widgetConfig.selectedLocations, widgetConfig.minRating, widgetConfig.hideReviewsWithoutComments, widgetConfig.settings.maxReviews]);

    // Platform options with icons from the utility
    const platforms = getPlatformsWithIcons();

    // Mock reviews for preview (fallback)
    const mockReviews = [
        {
            id: 1,
            reviewerName: 'John Smith',
            platform: { name: 'google' },
            rating: 5,
            reviewText: 'Excellent service and great products! Highly recommended.',
            reviewDate: '2024-01-15',
            reviewerAvatar: null
        },
        {
            id: 2,
            reviewerName: 'Sarah Johnson',
            platform: { name: 'facebook' },
            rating: 5,
            reviewText: 'Amazing experience from start to finish.',
            reviewDate: '2024-01-14',
            reviewerAvatar: null
        },
        {
            id: 3,
            reviewerName: 'Mike Wilson',
            platform: { name: 'yelp' },
            rating: 4,
            reviewText: 'Great quality and fast delivery.',
            reviewDate: '2024-01-13',
            reviewerAvatar: null
        }
    ];

    // Use actual reviews if available, otherwise use mock reviews
    const displayReviews = previewReviews.length > 0 ? previewReviews : mockReviews;

    // Handlers
    const handleTemplateSelect = (template) => {
        // Map template type to layout - explicit mapping for all supported templates
        const getLayoutFromType = (type) => {
            // Explicit mapping for all known template IDs
            const templateToLayoutMap = {
                // Carousel variants
                'carousel-widget': 'carousel',
                'simple-carousel': 'carousel',
                'reviews-carousel': 'carousel',
                // Dark carousel removed - components handle theming internally

                // Slider
                'slider': 'slider',

                // Grid variants
                'grid': 'grid',
                // Dark grid removed - components handle theming internally

                // List variants
                'list': 'list',

                // Masonry (note: template uses 'mansory' but layout uses 'masonry')
                'mansory': 'masonry',

                // Badge variants
                'badge': 'badge',
                'floating-badge': 'floating-badge',
                // Dark floating badge removed - components handle theming internally

                // Sidebar widget maps to list layout
                'sidebar-widget': 'list'
            };

            // Return explicit mapping or fallback to pattern matching
            if (templateToLayoutMap[type]) {
                return templateToLayoutMap[type];
            }

            // Fallback pattern matching for any new templates
            if (type?.includes('carousel')) return 'carousel';
            if (type?.includes('slider')) return 'slider';
            if (type?.includes('grid')) return 'grid';
            if (type?.includes('list')) return 'list';
            if (type?.includes('mansory')) return 'masonry';
            if (type?.includes('floating') && type?.includes('badge')) return 'floating-badge';
            if (type?.includes('badge')) return 'badge';
            if (type?.includes('sidebar')) return 'list';

            // Default fallback
            return 'carousel';
        };

        const selectedLayout = getLayoutFromType(template.id);

        setWidgetConfig(prev => ({
            ...prev,
            selectedTemplate: template,
            type: template.id,
            selectedLayout: selectedLayout // Automatically set the layout based on template
        }));
    };

    const handleLayoutSelect = (layoutId) => {
        setWidgetConfig(prev => ({
            ...prev,
            selectedLayout: layoutId
        }));
    };

    const handleWidgetConfigChange = (newConfig) => {
        setWidgetConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const handlePlatformToggle = (platformId) => {
        setWidgetConfig(prev => {
            const newSelectedPlatforms = prev.selectedPlatforms.includes(platformId)
                ? prev.selectedPlatforms.filter(id => id !== platformId)
                : [...prev.selectedPlatforms, platformId];

            return {
                ...prev,
                selectedPlatforms: newSelectedPlatforms
                // Don't reset template when platforms change - keep the selected template
            };
        });
    };

    const handleLocationToggle = (locationId) => {
        setWidgetConfig(prev => ({
            ...prev,
            selectedLocations: [locationId] // Single location selection only
        }));
    };

    const handleCreateLocation = async () => {
        if (!locationForm.name.trim() || !locationForm.address.street.trim() ||
            !locationForm.address.city.trim() || !locationForm.address.state.trim() ||
            !locationForm.address.zipCode.trim()) {
            alert('Please fill in all required fields');
            return;
        }

        setCreatingLocation(true);
        try {
            const response = await locationsAPI.createLocation(locationForm);

            // Add the new location to the list
            setAvailableLocations(prev => [response.data.data, ...prev]);

            // Reset form and close modal
            setLocationForm({
                name: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: 'United States'
                }
            });
            setShowCreateLocationModal(false);

            alert('Location created successfully!');
        } catch (error) {
            console.error('Error creating location:', error);
            const errorMessage = error.response?.data?.error || 'Error creating location. Please try again.';
            alert(errorMessage);
        } finally {
            setCreatingLocation(false);
        }
    };

    const handleLocationFormChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setLocationForm(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setLocationForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleInputChange = (field, value) => {
        setWidgetConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleWidgetNameChange = (newName) => {
        setWidgetConfig(prev => ({
            ...prev,
            name: newName
        }));
    };

    const handleClose = () => {
        if (isEditingMode) {
            // If editing, go back to widgets list
            navigate('/widgets');
        } else {
            // If creating new widget, go to dashboard
            navigate('/dashboard');
        }
    };

    const handleNext = () => {
        if (currentStep === 1 && widgetConfig.type !== 'whatsapp' && !widgetConfig.selectedTemplate) {
            alert('Please select a template first');
            return;
        }
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (isEditingMode) {
            // In edit mode, "back" should go back to widgets list
            navigate('/widgets');
        } else {
            // In create mode, go to previous step
            if (currentStep > 1) {
                setCurrentStep(currentStep - 1);
            }
        }
    };

    const handleCreateWidget = async () => {
        // Use default name if no name is provided
        const widgetName = widgetConfig.name?.trim() ||
            (widgetConfig.type === 'whatsapp' ? 'WhatsApp Chat Widget' :
                widgetConfig.type === 'appointment' ? 'Appointment Booking Widget' :
                    'Untitled All-in-One Reviews');

        if (!widgetConfig.type) {
            alert('Please select a layout');
            return;
        }

        // WhatsApp widget validation
        if (widgetConfig.type === 'whatsapp') {
            if (!widgetConfig.whatsappConfig?.phoneNumber?.trim()) {
                alert('Please enter a WhatsApp phone number');
                return;
            }

            // Validate phone number format (basic validation)
            const phoneNumber = widgetConfig.whatsappConfig.phoneNumber.trim();
            if (!/^\+?[\d\s-()]+$/.test(phoneNumber)) {
                alert('Please enter a valid phone number');
                return;
            }

            if (!widgetConfig.whatsappConfig?.message?.trim()) {
                alert('Please enter a pre-filled message');
                return;
            }
        }
        // Appointment widget validation
        else if (widgetConfig.type === 'appointment') {
            const validationResult = validateAppointmentWidget(widgetConfig);
            if (!validationResult.isValid) {
                // Set active tab and validation state to guide user
                if (appointmentWidgetConfigRef.current) {
                    appointmentWidgetConfigRef.current.handleValidationRedirect(
                        validationResult.targetTab,
                        validationResult.message,
                        validationResult.missingFields
                    );
                }
                return;
            }
        }
        // Review widget validation
        else {
            if (!widgetConfig.selectedTemplate) {
                alert('Please select a template');
                return;
            }

            if (widgetConfig.selectedLocations.length === 0) {
                alert('Please select at least one location');
                return;
            }

            if (widgetConfig.selectedPlatforms.length === 0 && platformCounts.length > 0) {
                alert('Please select at least one platform');
                return;
            }
        }

        setIsLoading(true);
        try {
            // Get platform data with icons for selected platforms
            const platformsWithIcons = getPlatformsWithIcons();
            const selectedPlatformData = widgetConfig.selectedPlatforms.map(platformId => {
                const platformInfo = platformsWithIcons.find(p => p.id === platformId);
                return {
                    id: platformId,
                    name: platformInfo?.name || platformId,
                    iconUrl: platformInfo?.iconUrl || '',
                    color: platformInfo?.color || '#6b7280'
                };
            });

            // Validate template data for review widgets only
            if (widgetConfig.type !== 'whatsapp' && widgetConfig.type !== 'appointment' && (!widgetConfig.selectedTemplate || !widgetConfig.selectedTemplate.id)) {
                alert('Please select a template before creating the widget.');
                return;
            }

            // Format locations for the backend with names
            const locations = widgetConfig.selectedLocations.map(locationId => {
                const location = availableLocations.find(loc => loc._id === locationId);
                return {
                    locationId: locationId,
                    name: location?.name || 'Unknown Location',
                    availablePlatforms: location?.availablePlatforms || []
                };
            });

            // Determine display mode based on style configuration, not template type
            const isDarkTheme = widgetConfig.style?.colorScheme === 'dark';
            const getLayoutFromType = (type) => {
                if (type?.includes('carousel') || type?.includes('slider')) return 'carousel';
                if (type?.includes('grid')) return 'grid';
                if (type?.includes('list')) return 'list';
                if (type?.includes('mansory')) return 'masonry';
                return 'carousel'; // default
            };

            let widgetPayload;

            // Create different payload structures for different widget types
            if (widgetConfig.type === 'whatsapp') {
                // WhatsApp Widget Payload
                widgetPayload = {
                    name: widgetName,
                    type: 'whatsapp',
                    whatsappConfig: {
                        phoneNumber: widgetConfig.whatsappConfig.phoneNumber.trim(),
                        message: widgetConfig.whatsappConfig.message.trim(),
                        welcomeMessage: widgetConfig.whatsappConfig.welcomeMessage || "Hi there 👋\n\nHow can I help you?",
                        profilePicture: widgetConfig.whatsappConfig.profilePicture || '',
                        name: widgetConfig.whatsappConfig.name || 'Support Team',
                        captionType: widgetConfig.whatsappConfig.captionType || 'online',
                        captionText: widgetConfig.whatsappConfig.captionText || 'Online',
                        showTooltip: widgetConfig.whatsappConfig.showTooltip !== false,
                        tooltipText: widgetConfig.whatsappConfig.tooltipText || 'Chat with us!',
                        size: widgetConfig.whatsappConfig.size || 'medium',
                        position: widgetConfig.whatsappConfig.position || 'bottom-right',
                        startChatMethod: widgetConfig.whatsappConfig.startChatMethod || 'button',
                        buttonText: widgetConfig.whatsappConfig.buttonText || 'Start Chat',
                        showButtonIcon: widgetConfig.whatsappConfig.showButtonIcon !== false,
                        messageFieldPlaceholder: widgetConfig.whatsappConfig.messageFieldPlaceholder || 'Type your message...',
                        includePageURL: widgetConfig.whatsappConfig.includePageURL || false
                    },
                    styleConfig: {
                        backgroundColor: widgetConfig.style?.backgroundColor || '#25D366'
                    }
                };
            } else if (widgetConfig.type === 'appointment') {
                // Appointment Widget Payload - Complete Settings
                widgetPayload = {
                    name: widgetName,
                    type: 'appointment',
                    appointmentConfig: {
                        // Template & Core Settings
                        templateType: widgetConfig.appointmentConfig?.templateType || 'inline-form',
                        widgetTitle: widgetConfig.appointmentConfig?.widgetTitle || 'Book Your Appointment',
                        widgetWidth: widgetConfig.appointmentConfig?.widgetWidth || '1240',
                        dateDisplayMode: widgetConfig.appointmentConfig?.dateDisplayMode || 'horizontal',

                        // Conversion Tracking (Google Ads + advanced snippet)
                        tracking: {
                            googleAds: {
                                enabled: widgetConfig.appointmentConfig?.tracking?.googleAds?.enabled || false,
                                conversionId: widgetConfig.appointmentConfig?.tracking?.googleAds?.conversionId || '',
                                conversionLabel: widgetConfig.appointmentConfig?.tracking?.googleAds?.conversionLabel || ''
                            },
                            customCode: widgetConfig.appointmentConfig?.tracking?.customCode || ''
                        },

                        // Services Configuration  
                        services: widgetConfig.appointmentConfig?.services || [],
                        serviceDisplayView: 'list', // Fixed to list view only

                        // Hours & Availability Settings
                        availableHours: widgetConfig.appointmentConfig?.availableHours || {
                            monday: { available: true, start: '08:00', end: '17:00' },
                            tuesday: { available: true, start: '08:00', end: '17:00' },
                            wednesday: { available: true, start: '08:00', end: '17:00' },
                            thursday: { available: true, start: '08:00', end: '17:00' },
                            friday: { available: true, start: '08:00', end: '17:00' },
                            saturday: { available: false, start: '08:00', end: '17:00' },
                            sunday: { available: false, start: '08:00', end: '17:00' }
                        },
                        dateSpecificHours: widgetConfig.appointmentConfig?.dateSpecificHours || [],
                        minAdvanceBooking: widgetConfig.appointmentConfig?.minAdvanceBooking || { value: 1, unit: 'hour' },
                        maxAdvanceBooking: widgetConfig.appointmentConfig?.maxAdvanceBooking || { value: 2, unit: 'months' },
                        timezone: widgetConfig.appointmentConfig?.timezone || '(GMT+05:30) Asia/Colombo',

                        // Business Profile Settings
                        businessProfile: {
                            showProfile: widgetConfig.appointmentConfig?.businessProfile?.showProfile !== false,
                            businessName: widgetConfig.appointmentConfig?.businessProfile?.businessName || '',
                            description: widgetConfig.appointmentConfig?.businessProfile?.description || '',
                            coverImage: widgetConfig.appointmentConfig?.businessProfile?.coverImage || '',
                            logo: widgetConfig.appointmentConfig?.businessProfile?.logo || '',
                            contact: {
                                phone: widgetConfig.appointmentConfig?.businessProfile?.contact?.phone || '',
                                whatsapp: widgetConfig.appointmentConfig?.businessProfile?.contact?.whatsapp || '',
                                instagram: widgetConfig.appointmentConfig?.businessProfile?.contact?.instagram || '',
                                address: widgetConfig.appointmentConfig?.businessProfile?.contact?.address || '',
                                email: widgetConfig.appointmentConfig?.businessProfile?.contact?.email || '',
                                website: widgetConfig.appointmentConfig?.businessProfile?.contact?.website || ''
                            }
                        },

                        // Notifications Configuration
                        notifications: {
                            clientConfirmation: widgetConfig.appointmentConfig?.notifications?.clientConfirmation !== false,
                            ownerNotification: widgetConfig.appointmentConfig?.notifications?.ownerNotification || false,
                            ownerEmail: widgetConfig.appointmentConfig?.notifications?.ownerEmail || '',
                            reminderEnabled: widgetConfig.appointmentConfig?.notifications?.reminderEnabled || false,
                            reminderTime: widgetConfig.appointmentConfig?.notifications?.reminderTime || { value: 24, unit: 'hours' },
                            emailTemplate: widgetConfig.appointmentConfig?.notifications?.emailTemplate || 'default'
                        },

                        // Integrations Configuration
                        integrations: {
                            googleCalendar: {
                                enabled: widgetConfig.appointmentConfig?.integrations?.googleCalendar?.enabled || false,
                                calendarId: widgetConfig.appointmentConfig?.integrations?.googleCalendar?.calendarId || '',
                                twoWaySync: widgetConfig.appointmentConfig?.integrations?.googleCalendar?.twoWaySync || false
                            },
                            googleSheets: {
                                enabled: widgetConfig.appointmentConfig?.integrations?.googleSheets?.enabled || false,
                                spreadsheetUrl: widgetConfig.appointmentConfig?.integrations?.googleSheets?.spreadsheetUrl || '',
                                sheetName: widgetConfig.appointmentConfig?.integrations?.googleSheets?.sheetName || 'Appointments'
                            }
                        },

                        // General Settings
                        settings: {
                            language: widgetConfig.appointmentConfig?.settings?.language || 'en-US',
                            allowRescheduling: widgetConfig.appointmentConfig?.settings?.allowRescheduling !== false,
                            allowCancellation: widgetConfig.appointmentConfig?.settings?.allowCancellation !== false,
                            requireCustomerInfo: widgetConfig.appointmentConfig?.settings?.requireCustomerInfo !== false,
                            collectNotes: widgetConfig.appointmentConfig?.settings?.collectNotes !== false,
                            autoConfirm: widgetConfig.appointmentConfig?.settings?.autoConfirm || false,
                            showPricing: widgetConfig.appointmentConfig?.settings?.showPricing !== false,
                            allowMultiplePerSlot: widgetConfig.appointmentConfig?.settings?.allowMultiplePerSlot === true
                        }
                    },

                    // Complete Style Configuration
                    styleConfig: {
                        // Core Colors & Typography
                        backgroundColor: widgetConfig.style?.backgroundColor || '#FFFFFF',
                        textColor: widgetConfig.style?.textColor || '#000000',
                        accentColor: widgetConfig.style?.accentColor || '#4A90E2',
                        fontFamily: widgetConfig.style?.fontFamily || 'default',

                        // Advanced Styling (only include if changed from defaults)
                        cornerRadius: widgetConfig.style?.cornerRadius ?? 8,
                        dropShadow: widgetConfig.style?.dropShadow ?? true,
                        responsive: widgetConfig.style?.responsive ?? true
                    }
                };
            } else {
                // Review Widget Payload (existing structure)
                widgetPayload = {
                    name: widgetName,
                    type: widgetConfig.type,
                    template: {
                        id: widgetConfig.selectedTemplate.id,
                        name: widgetConfig.selectedTemplate.name || 'Unknown Template',
                        image: widgetConfig.selectedTemplate.image || '',
                        category: widgetConfig.selectedTemplate.category || 'general'
                    },
                    selectedPlatforms: widgetConfig.selectedPlatforms.length > 0 ? widgetConfig.selectedPlatforms : [],
                    platformData: selectedPlatformData,
                    locations: locations,
                    settings: {
                        // Core display settings
                        maxReviews: widgetConfig.settings?.maxReviews || 5,
                        showRating: widgetConfig.settings?.showRating !== undefined ? widgetConfig.settings.showRating : true,
                        showAvatar: widgetConfig.settings?.showAvatar !== undefined ? widgetConfig.settings.showAvatar : true,
                        showDate: widgetConfig.settings?.showDate !== undefined ? widgetConfig.settings.showDate : true,
                        showPlatformLogo: widgetConfig.settings?.showPlatformLogo !== undefined ? widgetConfig.settings.showPlatformLogo : true,

                        // Animation settings
                        autoSlide: widgetConfig.settings?.autoSlide || false,
                        slideInterval: widgetConfig.settings?.slideInterval || 5000,

                        // Theme and styling
                        theme: isDarkTheme ? 'dark' : (widgetConfig.settings?.theme || 'light'),
                        primaryColor: widgetConfig.settings?.primaryColor || '#3b82f6',
                        borderRadius: widgetConfig.settings?.borderRadius || 8,

                        // Filtering settings
                        minRating: widgetConfig.minRating || 1,
                        hideReviewsWithoutComments: widgetConfig.hideReviewsWithoutComments || false,

                        // Layout and structure
                        layout: widgetConfig.selectedLayout || getLayoutFromType(widgetConfig.type),
                        displayMode: isDarkTheme ? 'dark' : 'light',

                        // Pagination
                        reviewsPerPage: widgetConfig.settings?.maxReviews || 5,

                        // Location settings
                        filterByLocation: true,
                        showLocationInfo: false,

                        // Custom styling (for future use)
                        customCSS: ''
                    },
                    // Header configuration (new fields)
                    headerConfig: widgetConfig.headerConfig || {
                        showHeader: true,
                        elements: {
                            heading: true,
                            rating: true,
                            numberOfReviews: true,
                            writeReviewButton: true
                        }
                    },
                    tabsConfig: widgetConfig.tabsConfig || {
                        showTabs: true,
                        elements: {
                            icon: true,
                            name: true,
                            rating: true
                        }
                    },
                    widgetTitleConfig: widgetConfig.widgetTitleConfig || {
                        showTitle: true,
                        title: "What our customers say",
                        caption: "Hello this is just a demo"
                    },
                    // Style configuration
                    styleConfig: widgetConfig.style || {
                        colorScheme: 'light',
                        accentColor: '#4A90E2',
                        fontFamily: 'default',
                        background: {
                            enabled: false,
                            type: 'color',
                            color: '#FFFFFF',
                            gradient: 'linear-gradient(45deg, #4A90E2, #9B59B6)'
                        },
                        widgetTitle: {
                            alignment: 'center',
                            titleColor: '#000000',
                            captionColor: '#666666',
                            titleSize: 24,
                            titleBold: false,
                            titleItalic: false,
                            captionSize: 15,
                            captionBold: false,
                            captionItalic: false
                        },
                        header: {
                            backgroundColor: '#FFFFFF',
                            textColor: '#000000',
                            sourceLogoColor: '#FF6B6B',
                            tabSourceIconColor: '#FF6B6B',
                            tabTextSize: 14,
                            cornerRadius: 8
                        },
                        rating: {
                            starsColor: '#F1C40F'
                        },
                        review: {
                            backgroundColor: '#FFFFFF',
                            outlineColor: '#E5E5E5',
                            textColor: '#000000',
                            reviewerNameColor: '#000000',
                            dateColor: '#666666',
                            textSize: 16,
                            cornerRadius: 8
                        },
                        pagination: {
                            color: '#4A90E2'
                        }
                    }
                };
            }

            let result;
            if (isEditingMode && editingWidgetId) {
                // Update existing widget
                result = await widgetsAPI.updateWidget(editingWidgetId, widgetPayload);
                setShowUpdateSuccessModal(true);
                return;
            } else {
                // Create new widget
                result = await widgetsAPI.createWidget(widgetPayload);
            }

            setCreatedWidget(result.data);
            setCurrentStep(3); // Move to deploy step for new widgets
        } catch (error) {
            console.error('Error saving widget:', error);
            const action = isEditingMode ? 'updating' : 'creating';
            const errorMsg = error.response?.data?.error || `Error ${action} widget. Please try again.`;
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            // Success will be handled by the modal in Step3Deploy component
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            setErrorMessage('Failed to copy to clipboard. Please try again.');
            setShowErrorModal(true);
        }
    };

    const resetWidget = () => {
        setWidgetConfig({
            name: '',
            type: '',
            selectedTemplate: null,
            selectedLayout: 'carousel',
            platform: 'all',
            selectedPlatforms: [],
            locations: [],
            selectedLocations: [],
            minRating: 1,
            hideReviewsWithoutComments: false,
            settings: {
                maxReviews: 5,
                showRating: true,
                showAvatar: true,
                showDate: true,
                showPlatformLogo: true,
                autoSlide: false,
                slideInterval: 5000,
                theme: 'light',
                primaryColor: '#3b82f6',
                borderRadius: 8
            },
            // Reset header configuration to defaults
            headerConfig: {
                showHeader: true,
                elements: {
                    heading: true,
                    rating: true,
                    numberOfReviews: true,
                    writeReviewButton: true
                }
            },
            tabsConfig: {
                showTabs: true,
                elements: {
                    icon: true,
                    name: true,
                    rating: true
                }
            },
            widgetTitleConfig: {
                showTitle: true,
                title: "What our customers say",
                caption: "Hello this is just a demo"
            },
            // Reset style configuration to defaults
            style: {
                colorScheme: 'light',
                accentColor: '#4A90E2',
                fontFamily: 'default',
                background: {
                    enabled: false,
                    type: 'color',
                    color: '#FFFFFF',
                    gradient: 'linear-gradient(45deg, #4A90E2, #9B59B6)'
                },
                widgetTitle: {
                    alignment: 'center',
                    titleColor: '#000000',
                    captionColor: '#666666',
                    titleSize: 24,
                    titleBold: false,
                    titleItalic: false,
                    captionSize: 15,
                    captionBold: false,
                    captionItalic: false
                },
                header: {
                    backgroundColor: '#FFFFFF',
                    textColor: '#000000',
                    sourceLogoColor: '#FF6B6B',
                    tabSourceIconColor: '#FF6B6B',
                    tabTextSize: 14,
                    cornerRadius: 8
                },
                rating: {
                    starsColor: '#F1C40F'
                },
                review: {
                    backgroundColor: '#FFFFFF',
                    outlineColor: '#E5E5E5',
                    textColor: '#000000',
                    reviewerNameColor: '#000000',
                    dateColor: '#666666',
                    textSize: 16,
                    cornerRadius: 8
                },
                pagination: {
                    color: '#4A90E2'
                }
            }
        });
        setCreatedWidget(null);
        setCurrentStep(1);
        setPreviewReviews([]);

        // Reset edit mode
        setIsEditingMode(false);
        setEditingWidgetId(null);

        // Clear URL parameters
        setSearchParams({});
    };

    // Enhanced Loading Skeleton for editing mode
    const EditWidgetSkeleton = () => (
        <div className="min-h-screen bg-gray-50">
            {/* Header Skeleton */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 animate-pulse">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-6 h-6 bg-gray-300 rounded"></div>
                        <div className="h-6 bg-gray-300 rounded w-48"></div>
                    </div>
                    <div className="w-6 h-6 bg-gray-300 rounded"></div>
                </div>
            </div>

            <div className="p-6 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column - Configuration Skeleton */}
                    <div className="space-y-6">
                        {/* Widget Name Skeleton */}
                        <div className="bg-white rounded-xl p-6 border animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-32 mb-3"></div>
                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                        </div>

                        {/* Locations Skeleton */}
                        <div className="bg-white rounded-xl p-6 border animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-40 mb-4"></div>
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex items-center space-x-3">
                                        <div className="w-5 h-5 bg-gray-300 rounded"></div>
                                        <div className="h-4 bg-gray-200 rounded flex-1"></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Platforms Skeleton */}
                        <div className="bg-white rounded-xl p-6 border animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-48 mb-4"></div>
                            <div className="grid grid-cols-2 gap-3">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="flex items-center space-x-3 p-3 border rounded-lg">
                                        <div className="w-6 h-6 bg-gray-300 rounded"></div>
                                        <div className="flex-1">
                                            <div className="h-4 bg-gray-200 rounded w-20 mb-1"></div>
                                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Settings Skeleton */}
                        <div className="bg-white rounded-xl p-6 border animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-36 mb-4"></div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    <div className="w-12 h-6 bg-gray-300 rounded-full"></div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                                    <div className="w-16 h-8 bg-gray-300 rounded"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Preview Skeleton */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl p-6 border animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-32 mb-4"></div>
                            <div className="aspect-video bg-gray-200 rounded-lg"></div>
                        </div>

                        {/* Action Buttons Skeleton */}
                        <div className="flex space-x-4">
                            <div className="h-12 bg-gray-300 rounded-lg flex-1"></div>
                            <div className="h-12 bg-gray-300 rounded-lg flex-1"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Show loading skeleton while fetching existing widget data
    if (loadingExistingWidget) {
        return <EditWidgetSkeleton />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Step 1: Select Layout Template */}
            {currentStep === 1 && (
                <Step1Layout
                    widgetConfig={widgetConfig}
                    onTemplateSelect={handleTemplateSelect}
                    onNext={handleNext}
                />
            )}

            {/* Step 2: Configure Widget */}
            {currentStep === 2 && widgetConfig.type === 'whatsapp' && (
                <WhatsAppWidgetConfig
                    widgetConfig={widgetConfig}
                    onWidgetConfigChange={handleWidgetConfigChange}
                    onCreateWidget={handleCreateWidget}
                    isLoading={isLoading}
                    onClose={handleClose}
                    onBack={handlePrevious}
                />
            )}

            {/* Step 2: Configure Appointment Widget */}
            {currentStep === 2 && widgetConfig.type === 'appointment' && (
                <AppointmentWidgetConfig
                    ref={appointmentWidgetConfigRef}
                    widgetConfig={widgetConfig}
                    onWidgetConfigChange={handleWidgetConfigChange}
                    onCreateWidget={handleCreateWidget}
                    isLoading={isLoading}
                    onClose={handleClose}
                    onBack={handlePrevious}
                />
            )}

            {/* Step 2: Configure Review Widget */}
            {currentStep === 2 && widgetConfig.type !== 'whatsapp' && widgetConfig.type !== 'appointment' && (
                <Step2Configure
                    widgetConfig={widgetConfig}
                    onInputChange={handleInputChange}
                    availableLocations={availableLocations}
                    loadingLocations={loadingLocations}
                    onLocationToggle={handleLocationToggle}
                    onShowCreateLocationModal={() => setShowCreateLocationModal(true)}
                    platforms={platforms}
                    platformCounts={platformCounts}
                    loadingPlatformCounts={loadingPlatformCounts}
                    onPlatformToggle={handlePlatformToggle}
                    onLayoutSelect={handleLayoutSelect}
                    onWidgetConfigChange={handleWidgetConfigChange}
                    previewMode={previewMode}
                    setPreviewMode={setPreviewMode}
                    previewReviews={displayReviews}
                    loadingPreviewReviews={loadingPreviewReviews}
                    onBack={handlePrevious}
                    onCreateWidget={handleCreateWidget}
                    isLoading={isLoading}
                    onWidgetNameChange={handleWidgetNameChange}
                    isEditingMode={isEditingMode}
                    onClose={handleClose}
                />
            )}

            {/* Step 3: Deploy Widget */}
            {currentStep === 3 && (
                <DashboardLayout>
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Deploy Widget</h1>
                                <p className="text-gray-600 mt-1">Your widget is ready! Copy the code and add it to your website.</p>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span className="bg-gray-100 px-3 py-1 rounded-full">Step 3 of 3</span>
                            </div>
                        </div>
                    </div>
                    <Step3Deploy
                        createdWidget={createdWidget}
                        widgetConfig={widgetConfig}
                        onCopyToClipboard={copyToClipboard}
                        onResetWidget={resetWidget}
                    />
                </DashboardLayout>
            )}

            {/* Modals */}
            <UpdateSuccessModal
                isOpen={showUpdateSuccessModal}
                onClose={() => setShowUpdateSuccessModal(false)}
                onRedirect={() => navigate('/widgets')}
            />

            <ErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                errorMessage={errorMessage}
            />

            {/* Location Modal */}
            {showCreateLocationModal && (
                <CreateLocationModal
                    isOpen={showCreateLocationModal}
                    onClose={() => setShowCreateLocationModal(false)}
                    onSubmit={handleCreateLocation}
                    locationForm={locationForm}
                    onLocationFormChange={handleLocationFormChange}
                    isCreating={creatingLocation}
                />
            )}
        </div>
    );
};

export default CreateWidget; 